import * as Sentry from '@sentry/browser';
import { tracking } from '../../services/TrackingService';
import { errorPrefix } from '../../constants/constants';
import { getRandomId } from '../../helpers/helpers';
/**
 * Create an Aspera Error item and send data to Sentry or Segment as needed
 */
export class AsperaError {
    /**
     * Create an Aspera Error and send data to appropriate channels
     * @param message - Message that describes the error in human readable form
     * @param error - Error data from HTTP or JavaScript error (Do not send classes or recursive items)
     * @param sentryLog - Indicate if data should be sent to Sentry (this should only be used for logic/code errors)
     * @param segmentLog - Indicate if data should be sent to Segment (Amplitude)
     * @param logErrorOnly - Do not print error to console, only log it. Useful for non critical or redundant items to avoid noise
     */
    constructor(message, error, sentryLog, segmentLog = true, logErrorOnly) {
        /** Prefix to add in front of messages printed to console */
        this.messagePrefix = errorPrefix;
        this.message = message;
        this.errorData = error;
        if (!logErrorOnly)
            this.print();
        this.log();
        if (segmentLog) {
            this.sendToSegment();
        }
        if (sentryLog) {
            this.sendToSentry();
        }
    }
    /**
     * Send data to segment for tracking
     */
    sendToSegment() {
        tracking.track('Started Process', { process: this.message, successFlag: false, processType: 'error' });
    }
    /**
     * Send data to sentry for notifying developers that a problem exists
     */
    sendToSentry() {
        Sentry.withScope(scope => {
            scope.setExtra('message', this.message);
            Sentry.captureException(this.errorData);
        });
    }
    /**
     * Get error text with prefix and message
     *
     * @returns full string with prefix and message
     */
    get errorMessage() {
        return `${this.messagePrefix}: ${this.message}`;
    }
    /**
     * Print message to the console
     */
    print() {
        if (this.errorData) {
            console.error(this.errorMessage, this.errorData);
        }
        else {
            console.error(this.errorMessage);
        }
    }
    /**
     * Log message into message array on window for retrieval
     */
    log() {
        if (!Array.isArray(window.asperaLog))
            window.asperaLog = [];
        const now = new Date();
        if (this.errorData) {
            window.asperaLog.push({ message: this.errorMessage, data: this.errorData, date: now.toISOString(), id: getRandomId() });
        }
        else {
            window.asperaLog.push({ message: this.errorMessage, date: now.toISOString(), id: getRandomId() });
        }
    }
    /**
     * Create a JavaScript Error object for throwing
     *
     * @returns an error object that can be passed to an observable or Throw
     */
    getError() {
        return new Error(this.errorMessage);
    }
}
